import { patchState, signalStore, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';
import { catchError, filter, of, pipe, switchMap, tap } from 'rxjs';
import { SupervisorsState } from '../supervisors.interface';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { computed, inject } from '@angular/core';
import { InteractionServiceGateway } from '../../../service-gateways/interactions/interaction.service-gateway';
import { tapResponse } from '@ngrx/operators';
import { MessageService } from 'primeng/api';
import { Supervisor } from '@origin8-web/models/call-reviews';
import { SupervisorsRefreshService } from './supervisors-refresh.service';

const initialState: SupervisorsState = {
  supervisors: [],
  isLoading: false,
  filter: '',
};

export const withSupervisorsStoreFeature = () => {
  return signalStoreFeature(
    withState(initialState),
    withComputed((store) => {
      return {
        filteredSupervisors: computed(() =>
          store.supervisors().filter((s) => s.supervisorName.toLowerCase().includes(store.filter().toLowerCase())),
        ),
      };
    }),
    withMethods(
      (store, interactionService = inject(InteractionServiceGateway), messageService = inject(MessageService)) => {
        return {
          setFilter: (filter: string) => patchState(store, { filter }),
          loadSupervisorsForOrg: rxMethod<string | undefined>(
            pipe(
              filter((organization) => !!organization),
              tap(() => patchState(store, { isLoading: true })),
              switchMap((organization) => interactionService.getSupervisorsForOrg(organization!)),
              tapResponse({
                next: (supervisors) => patchState(store, { supervisors, isLoading: false }),
                error: () => {
                  patchState(store, { isLoading: false });
                  messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'An error occurred while loading the supervisors.',
                  });
                },
              }),
            ),
          ),
        };
      },
    ),
    withMethods(
      (
        store,
        interactionService = inject(InteractionServiceGateway),
        messageService = inject(MessageService),
        supervisorsRefresh = inject(SupervisorsRefreshService),
      ) => {
        return {
          deleteSupervisor: rxMethod<{ supervisor: Supervisor; organization: string }>(
            pipe(
              switchMap(({ supervisor, organization }) =>
                interactionService.deleteSupervisor(supervisor, organization).pipe(
                  tapResponse({
                    next: () => {
                      messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Supervisor deleted successfully',
                      });
                      store.loadSupervisorsForOrg(organization);
                      supervisorsRefresh.notifyChanged();
                    },
                    error: (e) => {
                      const message =
                        Array.isArray(e) && e[0]?.message
                          ? e[0].message
                          : typeof e === 'object' && e !== null && 'message' in e
                            ? (e as { message: string }).message
                            : 'Failed to delete supervisor';
                      messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: message,
                      });
                    },
                  }),
                  catchError(() => of(null)),
                ),
              ),
            ),
          ),
        };
      },
    ),
  );
};

export const SupervisorsStore = signalStore(withSupervisorsStoreFeature());
